import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attention, auth, isForbidden, isNotFound, messaging } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Messaging data layer (doc 01 comms surface). Thin React Query wrappers over the
 * `@cwos/api-client` messaging domain — the client never gates (server enforces
 * `messaging:read`/`:send`/`:manage` + per-channel membership); these hooks just
 * cache, poll and invalidate. No mock data ever originates here.
 */

export const messagingKeys = {
  all: ['messaging'] as const,
  channels: ['messaging', 'channels'] as const,
  channel: (id: string) => ['messaging', 'channel', id] as const,
  messages: (id: string) => ['messaging', 'channel', id, 'messages'] as const,
  mentions: ['messaging', 'mentions'] as const,
  thread: (channelId: string, rootId: string) =>
    ['messaging', 'channel', channelId, 'thread', rootId] as const,
};

export function useChannels() {
  return useQuery({
    queryKey: messagingKeys.channels,
    queryFn: () => messaging.listChannels(),
    refetchInterval: 60_000,
  });
}

export function useChannel(id: string) {
  return useQuery({
    queryKey: messagingKeys.channel(id),
    queryFn: () => messaging.getChannel(id),
    enabled: id !== '',
  });
}

export function useMessages(id: string) {
  return useQuery({
    // The full page envelope (F193): messages plus the server's nextCursor and
    // hasMore, so the thread pages history on the server's word instead of the
    // full-page heuristic.
    queryKey: messagingKeys.messages(id),
    queryFn: () => messaging.listMessagesPage(id, { limit: 100 }),
    enabled: id !== '',
    refetchInterval: 30_000,
  });
}

/**
 * The signed-in user's id, for roster affordances (e.g. the mention picker
 * offering everyone but yourself). SAME query key as `usePermissions`, so it
 * shares that cache entry rather than issuing a second /auth/me — and it works
 * under the component tests' mock boundary, which AuthProvider does not reach.
 */
export function useMeUserId(): string | null {
  const query = useQuery({
    queryKey: ['auth', 'me', 'permissions'],
    queryFn: () => auth.me(),
    staleTime: 5 * 60_000,
  });
  return query.data?.userId ?? null;
}

/**
 * YOUR unread mentions across your channels (F194 seam 3). The server's read
 * is own-only by construction; this just polls it for the badge. Rows leave
 * the list when you mark the channel read, so the thread invalidates this key
 * alongside the channel list.
 */
export function useMyMentions() {
  return useQuery({
    queryKey: messagingKeys.mentions,
    queryFn: () => messaging.listMyMentions(),
    refetchInterval: 60_000,
  });
}

export function useSendMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: messaging.SendMessageInput) => messaging.sendMessage(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.messages(id) });
      void qc.invalidateQueries({ queryKey: messagingKeys.channels });
    },
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: messaging.CreateChannelInput) => messaging.createChannel(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.channels });
    },
  });
}

export function useAddMember(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: messaging.AddMemberInput) => messaging.addMember(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.channel(id) });
    },
  });
}

export function useRemoveMember(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => messaging.removeMember(id, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.channel(id) });
    },
  });
}

/**
 * Add or remove YOUR reaction to a message, then refresh the thread.
 *
 * `mine` decides the direction so a second tap on your own reaction removes it
 * (the server enforces one-per-user-per-emoji regardless).
 */
export function useToggleReaction(channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      messageId,
      emoji,
      mine,
    }: {
      messageId: string;
      emoji: string;
      mine: boolean;
    }) =>
      mine ? messaging.removeReaction(messageId, emoji) : messaging.addReaction(messageId, emoji),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.messages(channelId) });
    },
  });
}

/**
 * The organisation's messaging policy (GIFs / reactions on or off). Hides the
 * affordances the server would refuse anyway, so the UI never offers a button
 * that returns an error. Server-side enforcement is the real gate.
 */
export function useMessagingPolicy() {
  return useQuery({
    queryKey: ['messaging', 'policy'] as const,
    queryFn: () => messaging.policy(),
    staleTime: 300_000,
  });
}

export function useSetMuted(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (muted: boolean) => messaging.setMuted(id, { muted }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.channels });
      void qc.invalidateQueries({ queryKey: messagingKeys.channel(id) });
    },
  });
}

// --- Presentation helpers ---------------------------------------------------

export interface KindMeta {
  label: string;
  variant: Severity;
}

/** Kind → badge label + severity tone. Announcements read as "look" (heads-up). */
export function kindMeta(kind: messaging.ChannelKind): KindMeta {
  switch (kind) {
    case 'announcement':
      return { label: 'Announcement', variant: 'look' };
    case 'dm':
      return { label: 'Direct', variant: 'plain' };
    case 'channel':
    default:
      return { label: 'Channel', variant: 'sky' };
  }
}

/** A human channel title — never a UUID. Falls back per kind when unnamed. */
export function channelTitle(channel: messaging.ChannelView): string {
  // The server names the conversation now — a DM is titled after the person you
  // are talking to. This used to fall straight through to "Direct message" for
  // every DM, so three conversations with three people rendered identically.
  const title = channel.title?.trim();
  if (title) return title;

  // The fallbacks below only run against an older server that does not send a
  // title. They are honest, and none of them is an identifier.
  const name = channel.name?.trim();
  if (name) return name;
  const counterpart = channel.counterpart?.displayName?.trim();
  if (channel.kind === 'dm') return counterpart || 'Direct message';
  if (channel.kind === 'announcement') return 'Announcements';
  return 'Untitled channel';
}

/**
 * Sender identity for a message row — name first, then role, then an honest
 * system/unknown label. NEVER the raw `senderUserId` (doc: identity, not a UUID).
 */
export function senderLabel(message: messaging.MessageView): string {
  if (message.kind === 'automated') return automatedLabel(message);
  const name = message.senderName?.trim();
  if (name) return name;
  if (message.kind === 'system') return 'System';
  const role = message.senderRole?.trim();
  if (role) return role;
  return 'Someone on the team';
}

/**
 * The attributed author line for an automated message (contract #7,
 * AUTOMATION-CAPABILITY-MODEL rule 3): "Automated · set up by {owner}" /
 * "Automated · organization rule" / the assistant label. One shared
 * implementation (`@cwos/api-client`) renders the closed vocabulary for every
 * surface; it never prints an identifier and never accepts free text — the
 * label derives from the STRUCTURED attribution only, so a message body
 * cannot dress itself as this line.
 */
export function automatedLabel(message: messaging.MessageView): string {
  return messaging.automatedAttributionLabel(message.automated ?? null);
}

/**
 * Initials for the sender avatar — the first letters of the first two words of
 * the resolved label ("Avery Chen" → "AC"). Identity photos are a later lane;
 * initials are what the data already carries. The unknown-sender fallback
 * ("Someone on the team") would produce a false-looking "SO", so anything that
 * is not a real name renders as "?" — honest, not decorative.
 */
export function senderInitials(message: messaging.MessageView): string {
  const name = message.senderName?.trim();
  if (!name) return '?';
  const words = name.split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const second = words.length > 1 ? (words.at(-1)?.[0] ?? '') : '';
  const initials = (first + second).toUpperCase();
  return initials || '?';
}

/**
 * WHEN THIS CONVERSATION LAST MOVED — the sort key and the timestamp a list row
 * shows. The last message is the conversation's own activity; `updatedAt` is
 * the fallback for a conversation with no messages yet (creation counts as
 * activity). Never `lastReadAt`: that is a fact about the reader, not the
 * conversation, and sorting on it pins whatever the reader ignored to the top.
 */
export function activityAt(channel: messaging.ChannelView): string {
  return channel.lastMessage?.at ?? channel.updatedAt;
}

// --- Threads (F194 seam 4) --------------------------------------------------

/**
 * ONE thread's replies, on demand.
 *
 * Only ever enabled once a reader has actually expanded a thread: a channel
 * page can carry dozens of roots, and fetching every thread up front would be a
 * request per root for content nobody asked to see. The reply COUNT does not
 * need this — the message list already carries the server's own count on the
 * root — so the collapsed affordance is honest before a single thread is read.
 */
export function useThread(channelId: string, rootId: string, enabled: boolean) {
  return useQuery({
    queryKey: messagingKeys.thread(channelId, rootId),
    queryFn: () => messaging.readThread(channelId, rootId, { limit: 200 }),
    enabled: enabled && channelId !== '' && rootId !== '',
  });
}

/** The main flow, with each page-local reply moved under the root it belongs to. */
export interface FoldedThreads {
  /** What renders in the conversation flow, in the order the server sent it. */
  rows: messaging.MessageView[];
  /** The replies THIS PAGE carries, keyed by the root they belong to. */
  repliesByRoot: Map<string, messaging.MessageView[]>;
}

/**
 * Fold replies under their thread root — from `threadRootId` and nothing else.
 *
 * The server states which conversation a message belongs to; this function only
 * moves rows around according to that statement. It never derives a thread from
 * `replyTo`, because a quote is a different fact (what a message answered) and
 * treating it as thread membership would invent threads the server never said
 * existed.
 *
 * A reply whose root is NOT on this page stays exactly where it is, rendering
 * its quote as it always has. Hiding it under a root the reader cannot see
 * would make a message vanish from the conversation it was said in.
 *
 * Pure and total; the input order is preserved.
 */
export function foldThreads(messages: readonly messaging.MessageView[]): FoldedThreads {
  const rootIds = new Set(
    messages.filter((m) => (m.threadRootId ?? null) === null).map((m) => m.id),
  );
  const repliesByRoot = new Map<string, messaging.MessageView[]>();
  const rows: messaging.MessageView[] = [];
  for (const message of messages) {
    const root = message.threadRootId ?? null;
    if (root !== null && rootIds.has(root)) {
      const existing = repliesByRoot.get(root);
      if (existing) existing.push(message);
      else repliesByRoot.set(root, [message]);
      continue;
    }
    rows.push(message);
  }
  return { rows, repliesByRoot };
}

/**
 * "1 reply" / "4 replies" — the collapsed thread's label.
 *
 * Takes the count the SERVER stated. A surface that counted the replies it
 * happened to be holding would under-report a thread whose replies had scrolled
 * out of the page, and there is no reading of "3 replies" that is true when the
 * thread has seven.
 */
export function replyCountLabel(count: number): string {
  return count === 1 ? '1 reply' : `${count} replies`;
}

// --- Thread layout: day separators + same-sender grouping -------------------

export interface MessageRowPlan {
  message: messaging.MessageView;
  /** Human day label to render ABOVE this message, when the day changes. */
  daySeparator: string | null;
  /**
   * True when this row continues the previous sender's run — same person,
   * within GROUP_WINDOW_MS, same day — so the identity header collapses and
   * only the body renders. System and automated rows never group, in either
   * direction: a machine line between two human messages breaks the run on
   * purpose, because attribution must restart after it (the `kind === 'user'`
   * checks below are that rule — only human rows ever continue a run).
   */
  grouped: boolean;
}

/** Two messages this close, from the same person, read as one turn. */
export const GROUP_WINDOW_MS = 5 * 60_000;

/** "Today" / "Yesterday" / a real date — the day separator's label. */
export function dayLabel(day: Date, now: Date = new Date()): string {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(day)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return day.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(day.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

/**
 * Turn an oldest-first message page into render rows: a day separator wherever
 * the local calendar day changes, and header collapse for a same-sender run.
 * Pure and total — a malformed date simply never matches, so it separates.
 */
export function planMessageRows(
  messages: messaging.MessageView[],
  now: Date = new Date(),
): MessageRowPlan[] {
  const rows: MessageRowPlan[] = [];
  let prev: messaging.MessageView | null = null;
  for (const message of messages) {
    const at = new Date(message.createdAt);
    const prevAt = prev ? new Date(prev.createdAt) : null;
    const sameDay = prevAt !== null && at.toDateString() === prevAt.toDateString();
    const daySeparator = sameDay ? null : dayLabel(at, now);
    const grouped =
      sameDay &&
      prev !== null &&
      message.kind === 'user' &&
      prev.kind === 'user' &&
      message.senderUserId !== null &&
      message.senderUserId === prev.senderUserId &&
      at.getTime() - (prevAt as Date).getTime() < GROUP_WINDOW_MS;
    rows.push({ message, daySeparator, grouped });
    prev = message;
  }
  return rows;
}

// --- Mention rendering (F194 seam 3) ----------------------------------------

/** One run of body text; `mention` names the person when the run is a mark. */
export interface BodySegment {
  text: string;
  mention: messaging.MessageMentionView | null;
}

/**
 * Split a message body into plain and mention runs, FROM SERVER-VALIDATED
 * SPANS ONLY. Free text can never create a mention: a "@name" the server did
 * not validate has no span here and renders as the plain text it is. Spans
 * that are malformed for THIS body (out of bounds, overlapping) are dropped
 * rather than trusted — the mark is a claim about the text, and a claim that
 * does not fit the text is not rendered as one.
 */
export function mentionSegments(
  body: string,
  mentions: readonly messaging.MessageMentionView[] | undefined,
): BodySegment[] {
  const spans = (mentions ?? [])
    .filter(
      (m): m is messaging.MessageMentionView & { span: { start: number; length: number } } =>
        m.span !== null &&
        Number.isInteger(m.span.start) &&
        Number.isInteger(m.span.length) &&
        m.span.start >= 0 &&
        m.span.length > 0 &&
        m.span.start + m.span.length <= body.length,
    )
    .sort((a, b) => a.span.start - b.span.start);

  const segments: BodySegment[] = [];
  let cursor = 0;
  for (const m of spans) {
    if (m.span.start < cursor) continue; // overlap: first mark wins
    if (m.span.start > cursor)
      segments.push({ text: body.slice(cursor, m.span.start), mention: null });
    segments.push({ text: body.slice(m.span.start, m.span.start + m.span.length), mention: m });
    cursor = m.span.start + m.span.length;
  }
  if (cursor < body.length) segments.push({ text: body.slice(cursor), mention: null });
  if (segments.length === 0 && body.length > 0) segments.push({ text: body, mention: null });
  return segments;
}

/**
 * WHAT THE MENTIONS READ IS ENTITLED TO SAY.
 *
 * `Messages.tsx` computed `myMentions.data?.length ?? 0` and rendered
 * "N mentions of you" from it. That single line collapsed four states:
 *
 *   loading          `data` undefined -> 0 -> the line disappears, which reads
 *                    as "nothing is waiting for you" before anything was asked
 *   403 / network    `data` undefined -> 0 -> same sentence, from a read that
 *                    never came back
 *   a real zero      `[]` -> 0
 *   a FULL PAGE      `limit` is capped at 50, so 50 rows print "50 mentions of
 *                    you" for somebody who has five hundred
 *
 * The last one is the founder's attention ruling of 2026-09-08 in miniature: a
 * count that silently stops counting has discarded the obligations past the cap.
 * The server now sends `windowExhausted`; this turns the query into the five
 * states `attention.sentenceOf` knows how to word, and the floor survives.
 */
export function mentionAttention(query: {
  data: messaging.MyMentionsPage | undefined;
  isError: boolean;
  error: unknown;
}): attention.Attention {
  // Refusal FIRST. A 403 is an answer about now, not a failure to retry, and it
  // is emphatically not a zero -- the same branch order `attentionLogic.ts`
  // fixes for the alert queue.
  if (query.isError) return attention.fromFailure(isForbidden(query.error));
  if (query.data === undefined) return attention.UNASKED;
  return attention.fromPage(query.data.mentions, query.data.windowExhausted);
}

/** Unread mentions per channel — the badge's shape, from the own-only read. */
export function mentionCountsByChannel(
  mentions: readonly messaging.MyMentionView[] | undefined,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const m of mentions ?? []) {
    counts.set(m.channelId, (counts.get(m.channelId) ?? 0) + 1);
  }
  return counts;
}

/**
 * A real, actionable error message for a screen to show, never a silent
 * failure. The API client throws `Error` subclasses whose `.message` is the
 * server's own human-readable reason (e.g. "This conversation has been
 * archived.") — that is what belongs on screen, not a generic string, when
 * the server sent one. Falls back to `fallback` for anything else (a network
 * failure, an unparsable response) rather than showing a raw stack trace.
 */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim()) return err.message;
  return fallback;
}

/**
 * "This conversation is not yours to open" — however the API spells it.
 *
 * The messaging domain used to answer 403 for a channel the caller is not in.
 * It now answers 404, because `docs/withholding-contract.md` §H says a resource
 * outside a scoped grant must be 404 — a 403 confirms the row exists, and a
 * channel id is a uuid in a URL that anybody can guess or be sent.
 *
 * A screen that branches on 403 alone would therefore have dropped the honest
 * "you're not in this conversation" panel and fallen through to the generic
 * "we couldn't load this" retry box, which invites a person to retry something
 * that will never succeed. Both statuses land in the same branch here for the
 * same reason they answer the same question: the conversation is not open to
 * this account, and nothing about that will change on a second attempt.
 *
 * THE LOCAL SPELLING IS GONE. This used to read `err instanceof ApiError &&
 * err.status === 404`, with a note saying the predicate belonged beside
 * `isForbidden` in `packages/api-client/src/errors.ts` and was written out here
 * only because that package was another lane's. It landed: `NotFoundError` is a
 * distinct class the transport throws for every 404, and `isNotFound` is its
 * predicate, exported beside `isForbidden`. So both halves of this question are
 * now asked in the same vocabulary, and neither is a status number a screen
 * decided to care about on its own.
 */
export function isUnreachable(err: unknown): boolean {
  return isForbidden(err) || isNotFound(err);
}
